import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import { Slider } from "@/components/ui/slider";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  getCalibration,
  previewCalibration,
  applyCalibration,
  type PreviewCandidate,
} from "@/lib/admin.calibration.functions";
import { rankCandidates, clampNudge, MAX_NUDGE, DEFAULT_AUTHORITY_WEIGHT } from "@/lib/calibration-scoring";

export const Route = createFileRoute("/admin/calibration")({
  head: () => ({ meta: [{ title: "Calibration · Admin · Tiger's Eye" }, { name: "robots", content: "noindex" }] }),
  component: CalibrationPage,
});

const pct = (n: number) => `${Math.round(n * 100)}%`;
const signed = (n: number) => `${n >= 0 ? "+" : ""}${n.toFixed(2)}`;

function CalibrationPage() {
  const qc = useQueryClient();
  const getFn = useServerFn(getCalibration);
  const previewFn = useServerFn(previewCalibration);
  const applyFn = useServerFn(applyCalibration);
  const { data, isLoading, error } = useQuery({ queryKey: ["admin", "calibration"], queryFn: () => getFn() });

  const [weight, setWeight] = useState(DEFAULT_AUTHORITY_WEIGHT);
  const [nudges, setNudges] = useState<Record<string, number | null>>({});
  const [sample, setSample] = useState("");
  const [candidates, setCandidates] = useState<PreviewCandidate[] | null>(null);

  useEffect(() => {
    if (!data) return;
    setWeight(data.authority_weight);
    setNudges(Object.fromEntries(data.rows.map((r) => [r.id, r.manual_delta])));
  }, [data]);

  const preview = useMutation({
    mutationFn: () => previewFn({ data: { text: sample } }),
    onSuccess: (r) => setCandidates(r.candidates),
    onError: (e) => toast.error(e instanceof Error ? e.message : "Preview failed"),
  });

  const apply = useMutation({
    mutationFn: () =>
      applyFn({
        data: {
          authority_weight: Math.round(weight * 100) / 100,
          nudges: Object.entries(nudges).map(([id, manual_delta]) => ({ id, manual_delta })),
        },
      }),
    onSuccess: () => {
      toast.success("Calibration applied. New reviews will use these settings.");
      qc.invalidateQueries({ queryKey: ["admin", "calibration"] });
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : "Could not apply"),
  });

  const ruleOverrides = useMemo(() => {
    const out: Record<string, number> = {};
    for (const r of data?.rows ?? []) {
      const n = nudges[r.id];
      out[r.rule_node_id] = n ?? r.learned_delta;
    }
    return out;
  }, [data, nudges]);

  const comparison = useMemo(() => {
    if (!candidates || !data) return null;
    const base = candidates.map((c) => ({ id: c.id, similarity: c.similarity, authority_tier: c.authority_tier, delta: c.delta }));
    const current = rankCandidates(base, data.authority_weight);
    const proposed = rankCandidates(base, weight, ruleOverrides);
    const curRank = new Map(current.map((c) => [c.id, c]));
    const byId = new Map(candidates.map((c) => [c.id, c]));
    return proposed.slice(0, 12).map((p) => ({
      ...p,
      title: byId.get(p.id)!.title,
      type: byId.get(p.id)!.type,
      before: curRank.get(p.id)!,
    }));
  }, [candidates, data, weight, ruleOverrides]);

  const dirty =
    !!data &&
    (Math.abs(weight - data.authority_weight) > 0.001 ||
      data.rows.some((r) => (nudges[r.id] ?? null) !== r.manual_delta));

  if (isLoading) return <p className="text-sm text-muted-foreground">Loading…</p>;
  if (error || !data) return <p className="text-sm text-destructive">{error instanceof Error ? error.message : "Failed to load"}</p>;

  return (
    <div className="space-y-10">
      <div>
        <p className="overline mb-2">Learning loop</p>
        <h2 className="font-serif text-2xl text-primary mb-2">Calibration</h2>
        <p className="text-sm text-muted-foreground max-w-2xl">
          Adjust how rules are ranked when matching a draft, try it on a sample, then apply. Nothing changes for students until you press Apply.
        </p>
      </div>

      <section className="border border-border bg-card p-5 space-y-4">
        <h3 className="font-serif text-lg">Ranking balance</h3>
        <div className="flex justify-between text-sm">
          <span>Source authority <strong>{pct(weight)}</strong></span>
          <span>Learned from feedback <strong>{pct(1 - weight)}</strong></span>
        </div>
        <Slider min={30} max={95} step={1} value={[Math.round(weight * 100)]} onValueChange={([v]) => setWeight(v / 100)} aria-label="Source authority weight" />
        <p className="text-xs text-muted-foreground">
          Live: {pct(data.authority_weight)} / {pct(1 - data.authority_weight)}
          {data.updated_at ? ` · last changed ${new Date(data.updated_at).toLocaleString("en-GB")}` : " · default"}
        </p>
      </section>

      <section className="border border-border bg-card p-5 space-y-4">
        <h3 className="font-serif text-lg">Per-rule nudges</h3>
        {data.rows.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            No learned adjustments yet. They appear here as marker feedback builds patterns linked to rules.
          </p>
        ) : (
          <ul className="divide-y divide-border">
            {data.rows.map((r) => {
              const manual = nudges[r.id];
              const effective = manual ?? r.learned_delta;
              return (
                <li key={r.id} className="py-4 space-y-2">
                  <div className="flex flex-wrap justify-between gap-2">
                    <div className="min-w-0">
                      <p className="font-medium break-words">{r.rule_title}</p>
                      <p className="text-xs text-muted-foreground">{r.scope} · {r.support} feedback items · learned {signed(r.learned_delta)}</p>
                      {r.patterns.map((p) => (
                        <p key={p.id} className="text-xs text-muted-foreground mt-1 break-words">Pattern ({p.status}): {p.hypothesis}</p>
                      ))}
                    </div>
                    <div className="text-sm font-mono">{signed(effective)}{manual !== null && manual !== undefined && " (manual)"}</div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Slider
                      min={-MAX_NUDGE * 100}
                      max={MAX_NUDGE * 100}
                      step={1}
                      value={[Math.round(effective * 100)]}
                      onValueChange={([v]) => setNudges((s) => ({ ...s, [r.id]: clampNudge(v / 100) }))}
                      aria-label={`Nudge for ${r.rule_title}`}
                    />
                    <Button variant="ghost" size="sm" disabled={manual === null || manual === undefined} onClick={() => setNudges((s) => ({ ...s, [r.id]: null }))}>
                      Use learned
                    </Button>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </section>

      <section className="border border-border bg-card p-5 space-y-4">
        <h3 className="font-serif text-lg">Preview on a sample draft</h3>
        <Textarea rows={5} value={sample} onChange={(e) => setSample(e.target.value)} placeholder="Paste a paragraph from a draft (at least 20 characters)…" />
        <Button onClick={() => preview.mutate()} disabled={sample.trim().length < 20 || preview.isPending}>
          {preview.isPending ? "Matching…" : "Show rankings"}
        </Button>
        {comparison && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-left text-xs text-muted-foreground">
                <tr><th className="py-2 pr-2">New</th><th className="pr-2">Was</th><th className="pr-2">Rule</th><th className="pr-2">Tier</th><th className="pr-2">Match</th><th className="pr-2">Score now → new</th></tr>
              </thead>
              <tbody className="divide-y divide-border">
                {comparison.map((c) => {
                  const move = c.before.rank - c.rank;
                  return (
                    <tr key={c.id}>
                      <td className="py-2 pr-2 font-mono">{c.rank}</td>
                      <td className="pr-2 font-mono">
                        {c.before.rank}{" "}
                        {move !== 0 && <span className={move > 0 ? "text-primary" : "text-destructive"}>{move > 0 ? `▲${move}` : `▼${-move}`}</span>}
                      </td>
                      <td className="pr-2 break-words">{c.title}<span className="block text-xs text-muted-foreground">{c.type}</span></td>
                      <td className="pr-2">{c.authority_tier}</td>
                      <td className="pr-2">{pct(c.similarity)}</td>
                      <td className="pr-2 font-mono whitespace-nowrap">{c.before.score.toFixed(3)} → {c.score.toFixed(3)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <p className="text-xs text-muted-foreground mt-2">Lower score ranks higher. Students' reviews use the top 8.</p>
          </div>
        )}
      </section>

      <div className="flex flex-wrap gap-3 items-center">
        <Button onClick={() => apply.mutate()} disabled={!dirty || apply.isPending}>
          {apply.isPending ? "Applying…" : "Apply to live reviews"}
        </Button>
        <Button variant="outline" disabled={!dirty} onClick={() => { setWeight(data.authority_weight); setNudges(Object.fromEntries(data.rows.map((r) => [r.id, r.manual_delta]))); }}>
          Discard changes
        </Button>
        <Button variant="ghost" onClick={() => setWeight(DEFAULT_AUTHORITY_WEIGHT)}>Reset balance to 65/35</Button>
        <span className="text-xs text-muted-foreground">Every apply is recorded in the audit log.</span>
      </div>
    </div>
  );
}
